import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'qr-code',
        path: 'qr-code',
        component: () => import('../pages/qr/QrPage.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'teams',
        path: 'teams',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { teamContext: 'list' },
      },
      {
        path: 'teams/:teamId',
        component: () => import('../pages/users/team/TeamLayout.vue'),
        meta: { teamContext: 'inside' },
        children: [
          {
            path: '',
            redirect: (to) => ({
              name: 'team-dashboard',
              params: { teamId: String(to.params.teamId) },
            }),
          },
          {
            name: 'team-dashboard',
            path: 'dashboard',
            component: () => import('../pages/users/team/TeamDashboard.vue'),
            meta: { teamContext: 'inside' },
          },
          {
            name: 'team-shifts',
            path: 'shifts',
            component: () => import('../pages/users/team/TeamShifts.vue'),
            meta: { teamContext: 'inside' },
          },
          {
            name: 'team-qr',
            path: 'qr',
            component: () => import('../pages/users/team/TeamQrPage.vue'),
            meta: { teamContext: 'inside' },
          },
          {
            name: 'team-invite',
            path: 'invite',
            component: () => import('../pages/users/team/TeamInvite.vue'),
            meta: { teamContext: 'inside' },
          },
          {
            name: 'team-profile',
            path: 'profile',
            component: () => import('../pages/users/team/TeamProfile.vue'),
            meta: { teamContext: 'inside' },
          },
        ],
      },
      // Backward-compatible alias so older links to /users keep working.
      {
        name: 'users',
        path: 'users',
        redirect: { name: 'teams' },
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'payouts',
        path: 'payouts',
        component: () => import('../pages/payout/PayoutsPage.vue'),
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        path: 'onboarding',
        meta: { isOnboardingFlow: true },
        component: () => import('../pages/auth/onboarding/OnboardingLayout.vue'),
        redirect: { name: 'onboarding-slide', params: { step: '1' } },
        children: [
          {
            path: ':step(\\d+)',
            name: 'onboarding-slide',
            component: () => import('../pages/auth/onboarding/OnboardingSlideView.vue'),
            props: true,
          },
        ],
      },
    ],
  },
  {
    path: '/complete-stripe-setup',
    component: AuthLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'complete-stripe-setup',
        component: () => import('../pages/auth/CompleteStripeSetup.vue'),
      },
    ],
  },
  {
    path: '/success',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'success',
        component: () => import('../pages/auth/SuccessPage.vue'),
      },
    ],
  },
  {
    path: '/t/activate/:publicSlug',
    name: 'kiosk-activate',
    component: () => import('../pages/kiosk/KioskActivatePage.vue'),
    meta: { public: true },
  },
  {
    path: '/auth',
    component: AuthLayout,
    meta: { guestOnly: true },
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup/Step1.vue'),
      },
      {
        name: 'verify',
        path: 'verify',
        redirect: () => {
          const authStore = useAuthStore()
          return authStore.userEmail?.trim() ? { name: 'signupfinal' } : { name: 'signup' }
        },
      },
      {
        name: 'signupfinal',
        path: 'signupfinal',
        component: () => import('../pages/auth/Signup/Step3.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password',
        component: () => import('../pages/auth/ResetPasswordFromLink.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  const token = localStorage.getItem('access_token')
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
  const guestOnly = to.matched.some((r) => r.meta.guestOnly)

  // 1️⃣ Нет токена → не авторизован
  if (!token) {
    if (requiresAuth) {
      return next({ name: 'login' })
    }
    return next()
  }

  // 2️⃣ Есть токен, но user ещё не загружен (skip в popup после Stripe — там нет доступа к auth)
  const isPopupReturn =
    typeof window !== 'undefined' && window.opener && to.path === '/complete-stripe-setup' && to.query?.onboarding
  if (token && !authStore.user && !isPopupReturn) {
    const ok = await authStore.fetchMe()
    if (!ok && requiresAuth) {
      return next({ name: 'login' })
    }
  }

  // 3️⃣ Гость пытается попасть на auth-страницы
  if (guestOnly && authStore.isAuthenticated) {
    // Allow escape to login (switch account) without dashboard → stripe trap
    if (to.name === 'login' || to.query.signout === '1') {
      authStore.logout()
      return next({ name: 'login', query: {}, replace: true })
    }
    return next({ name: 'dashboard' })
  }

  // 4️⃣ Stripe Connect: need a Connect account; status refresh for verification banner
  const isStripeSetupRoute = to.name === 'complete-stripe-setup' || to.path === '/complete-stripe-setup'
  if (
    authStore.user &&
    !authStore.user.stripe_connect_account_id &&
    !authStore.user.stripe_setup_deferred &&
    !isStripeSetupRoute &&
    !isPopupReturn &&
    requiresAuth
  ) {
    return next({ name: 'complete-stripe-setup' })
  }

  // 5️⃣ Onboarding slides: after Connect account is created
  const isOnboardingRoute = to.matched.some((r) => r.meta.isOnboardingFlow)
  if (
    authStore.user &&
    authStore.user.stripe_connect_account_id &&
    !authStore.user.has_seen_onboarding &&
    !isOnboardingRoute &&
    !isStripeSetupRoute &&
    requiresAuth
  ) {
    return next({ name: 'onboarding-slide', params: { step: '1' } })
  }

  next()
})
export default router
